import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import CollegeLogo from './CollegeLogo';
import { MonoLabel, EditorialButton } from './Editorial';
import CoachDetailModal from './CoachDetailModal';
import CollapsibleGroup from './schooldetail/CollapsibleGroup';
import ScoreGauge from './schooldetail/charts/ScoreGauge';
import SportsSection, { hasContent as hasSports } from './schooldetail/sections/SportsSection';
import CoachesSection, { hasContent as hasCoaches } from './schooldetail/sections/CoachesSection';
import AcademicsSection, { hasContent as hasAcademics } from './schooldetail/sections/AcademicsSection';
import FinanceSection, { hasContent as hasFinance } from './schooldetail/sections/FinanceSection';
import JobsSection, { hasContent as hasJobs } from './schooldetail/sections/JobsSection';
import CampusSection, { hasContent as hasCampus } from './schooldetail/sections/CampusSection';
import CrimeSection, { hasContent as hasCrime } from './schooldetail/sections/CrimeSection';
import {
  SportsIcon, CoachesIcon, AcademicsIcon, FinanceIcon, JobsIcon, CampusIcon, CrimeIcon,
} from './schooldetail/icons';
import './schooldetail/schooldetail.css';

// Human labels per dimension key. Kept in sync with MatchesPanel and the
// iOS/Android MatchReason label maps.
const DIMENSION_LABELS = {
  major: 'Major', sport: 'Sport', region: 'Region', size: 'Size',
  setting: 'Setting', type: 'Public/Private', division: 'Division',
  academics: 'Academics', roster_opportunity: 'Roster Spots',
  program_investment: 'Well-Resourced', scholarship: 'Athletic Aid',
  title_ix: "Women's Program", aid_fit: 'Aid Fit',
};

// Fixed section order + which dimensions belong to each (mirrors iOS).
//
// Web deliberately shows BOTH matched and missed signals here, grouped, where
// iOS/Android show only the matched ones in a single compact box. Knowing what
// you missed on is more useful to an athlete than knowing only what you hit.
// Candidate to backport to the apps — see the design spec, §6.
const REASON_SECTIONS = [
  { title: 'Athletic Fit', dims: ['division', 'sport'] },
  { title: 'Financial / Aid', dims: ['scholarship', 'program_investment', 'roster_opportunity', 'title_ix', 'aid_fit'] },
  { title: 'Academics', dims: ['academics'] },
  { title: 'Academic / Location', dims: ['major', 'region', 'size', 'setting', 'type'] },
];

// Accordion order and default-open set both mirror MatchDetailSheet.swift's
// `DetailSection` enum and `expanded: Set = [.coaches, .sports]`.
//
// Each section exports a `hasContent(detail)` predicate alongside its
// component, colocated so the two can't drift. An accordion header that opens
// onto nothing is worse than no accordion at all, so an empty section is
// dropped entirely — the same `sectionWrap(hasContent:)` contract the apps use.
const GROUPS = [
  { key: 'sports', title: 'Sports', icon: <SportsIcon />, Section: SportsSection, has: hasSports },
  { key: 'coaches', title: 'Coaches', icon: <CoachesIcon />, Section: CoachesSection, has: hasCoaches },
  { key: 'academics', title: 'Academics', icon: <AcademicsIcon />, Section: AcademicsSection, has: hasAcademics },
  { key: 'finance', title: 'Finance', icon: <FinanceIcon />, Section: FinanceSection, has: hasFinance },
  { key: 'jobs', title: 'Jobs', icon: <JobsIcon />, Section: JobsSection, has: hasJobs },
  { key: 'campus', title: 'Campus', icon: <CampusIcon />, Section: CampusSection, has: hasCampus },
  { key: 'crime', title: 'Crime', icon: <CrimeIcon />, Section: CrimeSection, has: hasCrime },
];

const DEFAULT_OPEN = ['sports', 'coaches'];

export default function MatchDetailModal({ match, onClose }) {
  const { API_URL } = useAuth();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [openGroups, setOpenGroups] = useState(DEFAULT_OPEN);
  // null until the Following list loads; then whether this school is in it.
  // Follow is one-way here — unfollow lives on the Following page.
  const [isFollowing, setIsFollowing] = useState(null);
  const [followBusy, setFollowBusy] = useState(false);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  useEffect(() => {
    if (!match) return undefined;
    let cancelled = false;
    setLoading(true);
    setDetail(null);
    axios.get(`${API_URL}/athletes/match-detail/${match.unitid}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    }).then((res) => {
      if (!cancelled) setDetail(res.data);
    }).catch((err) => {
      console.error('match-detail load failed', err);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [match, API_URL]);

  useEffect(() => {
    if (!match) return undefined;
    let cancelled = false;
    setIsFollowing(null);
    axios.get(`${API_URL}/athletes/following`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    }).then((res) => {
      if (!cancelled) setIsFollowing((res.data || []).some((r) => r.unitid === match.unitid));
    }).catch((err) => {
      console.error('following load failed', err);
    });
    return () => { cancelled = true; };
  }, [match, API_URL]);

  const reasons = useMemo(() => (match?.reasons || []), [match]);
  const reasonByDim = useMemo(() => {
    const m = {};
    reasons.forEach((r) => { m[r.dimension] = r; });
    return m;
  }, [reasons]);
  // Server-supplied. Names sections that exist but were withheld for tier —
  // never sections that were simply empty (see the design spec §3.2). Sourced
  // from `detail` directly (not `d0`) because this must run unconditionally,
  // above the `if (!match) return null;` early return below — `d0` is
  // defined after it.
  const lockedKeys = useMemo(() => new Set(detail?.locked || []), [detail?.locked]);

  if (!match) return null;

  const d0 = detail || {};
  const name = match.name || d0.name || '';
  const logoUrl = match.logo_url ?? d0.logo_url;
  const matchedCount = reasons.filter((r) => r.matched).length;
  const subtitle = [match.city ?? d0.city, match.state ?? d0.state, match.division ?? d0.division]
    .map((s) => (s || '').trim()).filter(Boolean).join(' · ');

  // A school opened from search that isn't in the athlete's match set arrives
  // with score 0 and no reasons. Showing an empty ring would read as "this
  // school scored zero for you", which is false — so hide the whole apparatus.
  // Same guard as `match.score != 0 || !match.reasons.isEmpty` on iOS.
  const isScored = match.score !== 0 || reasons.length > 0;

  const toggleGroup = (key) => {
    setOpenGroups((prev) => (
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    ));
  };

  const renderReason = (r) => (
    <div key={r.dimension} className="ed-row" style={{ gap: 10, alignItems: 'flex-start', padding: '8px 0' }}>
      <span aria-hidden="true" style={{ fontSize: 15, lineHeight: '20px', flexShrink: 0, color: r.matched ? 'var(--ed-accent-green)' : '#94a3b8' }}>
        {r.matched ? '✓' : '✗'}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 500 }}>{DIMENSION_LABELS[r.dimension] || r.dimension}</div>
        {r.value && <div style={{ fontSize: 14, fontWeight: 600 }}>{r.value}</div>}
        {r.benchmark && <div className="ed-muted" style={{ fontSize: 12 }}>{r.benchmark}</div>}
      </div>
    </div>
  );

  // Admissions and grad-rate URLs are omitted server-side for Free (spec
  // §3.2). Do not add a fallback here — a client-side fallback would put
  // back exactly what the server withheld.
  const links = d0.links || {};
  const linkRows = [
    ['Admissions page', links.admissions_url],
    ['Athletics grad rates', links.athletics_gradrate_url],
    ['School website', links.website_url],
  ].filter(([, url]) => Boolean(url));

  const handleFollow = async () => {
    if (isFollowing !== false || followBusy) return;
    setFollowBusy(true);
    try {
      await axios.post(`${API_URL}/athletes/following`, {
        unitid: match.unitid,
        college_name: name,
        college_city: match.city ?? d0.city ?? '',
        college_state: match.state ?? d0.state ?? '',
        news_enabled: true,
        follow_coach: true,
        topic_recruiting: false,
        topic_games: false,
        topic_nil: false,
        topic_development: false,
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setIsFollowing(true);
    } catch (err) {
      console.error('Error following school:', err);
    }
    setFollowBusy(false);
  };

  return (
    <>
      <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="match-detail-title" onClick={onClose}>
        <div className="modal-panel sd" style={{ maxWidth: 760 }} onClick={(e) => e.stopPropagation()} data-testid="match_detail_sheet">
          <button className="modal-close" onClick={onClose} aria-label="Close">&times;</button>

          <div className="ed-row" style={{ gap: 14, alignItems: 'center', paddingRight: 40 }}>
            <CollegeLogo schoolName={name} logoUrl={logoUrl} unitid={match.unitid} size={64} />
            <div style={{ minWidth: 0 }}>
              <h2 id="match-detail-title" style={{ margin: 0, fontSize: 20, fontWeight: 700, letterSpacing: '-0.3px' }}>{name}</h2>
              {subtitle && <div className="ed-muted" style={{ fontSize: 13, marginTop: 3 }}>{subtitle}</div>}
            </div>
            {isScored && <ScoreGauge score={match.score} />}
            {isFollowing != null && (
              <EditorialButton
                variant="ghost"
                onClick={handleFollow}
                disabled={isFollowing || followBusy}
                style={{ marginLeft: 'auto', flexShrink: 0, padding: '6px 12px' }}
                aria-label={isFollowing ? 'Following this school' : 'Follow this school'}
                data-testid="match_detail_follow"
              >
                {isFollowing ? '✓ Following' : '+ Follow'}
              </EditorialButton>
            )}
          </div>

          {isScored && (
            <div className="ed-row ed-row--between" style={{ marginTop: 16 }}>
              <MonoLabel>{matchedCount} OF {reasons.length} SIGNALS MATCHED</MonoLabel>
            </div>
          )}

          {REASON_SECTIONS.map((section) => {
            const rs = section.dims.map((k) => reasonByDim[k]).filter(Boolean);
            if (rs.length === 0) return null;
            return (
              <div key={section.title} style={{ marginTop: 20, borderTop: '1px solid #e2e8f0', paddingTop: 14 }}>
                <MonoLabel>{section.title}</MonoLabel>
                <div style={{ marginTop: 6 }}>{rs.map(renderReason)}</div>
              </div>
            );
          })}

          {loading && <p className="ed-muted" style={{ fontSize: 13, marginTop: 18 }}>Loading school details…</p>}

          {detail && (
            <div style={{ marginTop: 18 }}>
              {GROUPS.filter(({ key, has }) => has(detail) || lockedKeys.has(key))
                     .map(({ key, title, icon, Section }) => (
                <CollapsibleGroup
                  key={key}
                  id={`match_detail_group_${key}`}
                  title={title}
                  icon={icon}
                  locked={lockedKeys.has(key)}
                  isExpanded={openGroups.includes(key)}
                  onToggle={() => toggleGroup(key)}
                >
                  <Section detail={detail} onSelectCoach={setSelectedCoach} />
                </CollapsibleGroup>
              ))}

              {/* Gate on a locked ACCORDION, not on any entry in `locked`.
                  `locked` also names withheld LINKS (admissions, grad rates),
                  and `lockedKeys.size > 0` fired the "Go Pro to open these"
                  prompt when only a link was withheld — pointing the athlete
                  at nothing. */}
              {GROUPS.some(({ key }) => lockedKeys.has(key)) && (
                <p className="ed-muted" style={{ fontSize: 13, marginTop: 14 }}
                   data-testid="match_detail_research_locked">
                  Go Pro to open these — see which colleges fit you, and why.
                </p>
              )}

              {linkRows.length > 0 && (
                <div style={{ marginTop: 20, borderTop: '1px solid #e2e8f0', paddingTop: 14 }}>
                  <MonoLabel>Links</MonoLabel>
                  <div style={{ marginTop: 6 }}>
                    {linkRows.map(([label, url]) => (
                      <div key={label} className="ed-row ed-row--between" style={{ gap: 12, padding: '6px 0', alignItems: 'baseline' }}>
                        <span className="ed-muted" style={{ fontSize: 13 }}>{label}</span>
                        <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 14, fontWeight: 600 }}>Open ↗</a>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {selectedCoach && (
        <CoachDetailModal
          coach={selectedCoach}
          schoolName={name}
          onClose={() => setSelectedCoach(null)}
        />
      )}
    </>
  );
}
